import {AfterViewInit, Component, ElementRef, inject, viewChild} from '@angular/core';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {Table, TableLazyLoadEvent, TableModule} from 'primeng/table';
import {QualificationListItem} from '../../../model/qualification-list-item.model';
import {QualificationService} from '../../../service/qualification.service';
import {CantaaMessageService} from '../../../service/cantaa-message.service';
import {CantaaErrorHandlerService} from '../../../service/cantaa-error-handler.service';
import {GridHelperService} from '../../../service/helper/grid-helper.service';
import {FocusHelper} from '../../../service/helper/focus-helper';
import {GridRequest} from '../../../model/grid-request.model';
import {Permission} from '../../../constants/Permission';
import {HasPermissionDirective} from '../../../directive/has-permission.directive';
import {CantaaGridMeta} from "../../../model/GridMeta";
import {GridMetaService} from "../../../service/grid-meta.service";

@Component({
  selector: 'wim-qualification-grid',
  standalone: true,
  imports: [
    TableModule,
    FormsModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatIconModule,
    MatIconButton,
    HasPermissionDirective
  ],
  templateUrl: './qualification-grid.component.html',
  styleUrl: './qualification-grid.component.scss'
})
export class QualificationGridComponent implements AfterViewInit {
  dataTable = viewChild.required<Table>('dt');
  protected readonly Permission = Permission;
  readonly gridName = 'QUALIFICATION_GRID';

  private gridMetaService = inject(GridMetaService);
  private fb = inject(FormBuilder);
  private messageService = inject(CantaaMessageService);
  private errorHandler = inject(CantaaErrorHandlerService);
  private el = inject(ElementRef);
  private gridHelperService = inject(GridHelperService);
  private qualificationService = inject(QualificationService);

  qualifications: QualificationListItem[] = [];
  totalRecords: number = 0;
  editMode = false;

  createNewForm = this.fb.group({
    name: ['', Validators.required],
  });

  gridMeta!: CantaaGridMeta;
  clonedItems: { [s: number]: QualificationListItem } = {};

  constructor() {
    this.initGridMeta();
  }

  private initGridMeta() {
    const defaultSortMeta = [{field: 'name', order: 1}];
    this.gridMeta = this.gridMetaService.getGridMeta(this.gridName, defaultSortMeta);
  }

  async ngAfterViewInit() {
    await this.reloadData();
  }

  onRowEditInit(qualification: QualificationListItem) {
    this.clonedItems[qualification.id] = { ...qualification };
    this.editMode = true;
  }

  async onRowEditSave(qualification: QualificationListItem) {
    await this.save(qualification);
    this.editMode = false;
    await this.reloadData();
  }

  private async save(qualification: QualificationListItem) {
    try {
      await this.qualificationService.saveQualification(qualification);
      this.messageService.success('SAVE.SUCCESS');
    } catch (e) {
      this.errorHandler.handleError(e, 'SAVE.FAILED');
    }
  }

  onRowEditCancel(documentType: QualificationListItem, index: number) {
    this.qualifications[index] = this.clonedItems[documentType.id];
    delete this.clonedItems[documentType.id];
    this.editMode = false;
  }

  async onCreateNew() {
    const qualification = {
      name: this.createNewForm.value.name
    } as QualificationListItem;

    await this.save(qualification);
    await this.reloadData();
    this.createNewForm.reset();
    Object.values(this.createNewForm.controls).forEach((control) => {
      control?.setErrors(null);
      control?.markAsPristine();
      control?.markAsUntouched();
    });
    FocusHelper.focusFormField(this.el, 'name');
  }

  onCancel() {
    this.createNewForm.reset();
  }

  async onUpdateStatus(qualification: QualificationListItem) {
    try {
      await this.qualificationService.updateQualificationStatus(qualification.id, !qualification.active);
      this.messageService.success('UPDATE.SUCCESS');
      await this.reloadData();
    } catch (e) {
      this.errorHandler.handleError(e, 'UPDATE.FAILED');
    }
  }

  async fetchDataViaTableComponent(lazyLoadMeta: TableLazyLoadEvent) {
    this.gridMetaService.setGridMeta(this.gridName, {lazyLoadMeta});
    let gridRequest = this.gridHelperService.createGridRequest(lazyLoadMeta);
    await this.fetchDAta(gridRequest);
  }

  async reloadData() {
    let lazyLoadMetadata = this.dataTable().createLazyLoadMetadata();
    let gridRequest = this.gridHelperService.createGridRequest(lazyLoadMetadata);
    await this.fetchDAta(gridRequest);
  }

  async fetchDAta(gridRequest: GridRequest) {
    try {
      const collection = await this.qualificationService.getPagedQualifications(gridRequest);
      this.qualifications = collection.items;
      this.totalRecords = collection.totalNumberOfItems;
    } catch (e) {
      this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
    }
  }

  getTdClass(item: any) {
    return item.active ? "" : 'strikethrough';
  }

}
